import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { InteractionListQuery } from '@inbound/contract';
import {
  CALL_REASON_FILTERS,
  CALL_RESOLUTION_FILTERS,
  callReasonLabels,
  callResolutionLabels,
} from '@inbound/contract';

import { useGetInteractionsQuery } from '../../store/interactions-api.js';
import { useGetLinesQuery } from '../../store/lines-api.js';
import { messageOf } from '../../store/error-message.js';
import { formatDuration } from '../../ui/duration.js';
import { IconDownload, IconInteractions, IconSearch } from '../../ui/icons.js';
import { PAGE_SIZES, Pagination } from '../../ui/Pagination.js';
import { when } from '../../ui/when.js';
import { InteractionDrawer } from './InteractionDrawer.js';
import { ReasonChip, ResolutionChip } from './OutcomeChips.js';
import { barGeometry, formatScore } from './sentiment.js';
import styles from './InteractionsPage.module.css';

/** How long to wait before turning keystrokes into a request. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * How often to look for newly analysed calls.
 *
 * Nothing about an interaction changes once it exists, but rows *appear*, and
 * on an inbound line they appear whenever a caller rings — an analysis lands
 * seconds after the call it describes ends.
 */
const POLL_MS = 10_000;

type Filters = Omit<InteractionListQuery, 'page' | 'pageSize'>;

/** The filters, read from the URL. Anything unrecognised is dropped, not trusted. */
function readFilters(params: URLSearchParams): Filters {
  const pick = <T extends string>(key: string, allowed: readonly T[]): T | undefined => {
    const value = params.get(key);
    return value !== null && (allowed as readonly string[]).includes(value)
      ? (value as T)
      : undefined;
  };
  const day = (key: string): string | undefined => {
    const value = params.get(key);
    return value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;
  };
  const found = {
    search: params.get('search')?.trim() || undefined,
    reason: pick('reason', CALL_REASON_FILTERS),
    resolution: pick('resolution', CALL_RESOLUTION_FILTERS),
    lineId: params.get('lineId') || undefined,
    from: day('from'),
    to: day('to'),
  };
  // Absent keys rather than undefined ones, so "no filter" is an empty object.
  return Object.fromEntries(
    Object.entries(found).filter(([, value]) => value !== undefined),
  ) as Filters;
}

/**
 * Interactions (S08).
 *
 * ## Why this page is not a list of calls
 *
 * An interaction is a call that has been *analysed*. A call where the caller
 * never spoke has nothing to analyse and a failed call has no transcript, so
 * neither appears here — they count on the dashboard instead. Every row on this
 * page carries a conclusion an operator can act on.
 *
 * ## Why the filters live in the URL
 *
 * So a filtered view can be shared, survives a reload, and the browser's Back
 * button undoes a filter. The open interaction is part of the URL for the same
 * reason (`/interactions/:id`), and closing it returns to the same filtered list.
 * Filtering happens on the server, which also makes the export exact.
 */
export function InteractionsPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const { interactionId } = useParams<{ interactionId: string }>();
  const navigate = useNavigate();

  const filters = useMemo(() => readFilters(params), [params]);
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const sizeParam = Number.parseInt(params.get('pageSize') ?? '', 10);
  const pageSize = (PAGE_SIZES as readonly number[]).includes(sizeParam)
    ? sizeParam
    : PAGE_SIZES[0];

  /** Change the URL. Any filter change goes back to the first page. */
  const update = (changes: Record<string, string | null>, keepPage = false): void => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (value === null || value === '') next.delete(key);
          else next.set(key, value);
        }
        if (!keepPage) next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  // The search box is typed into freely and written to the URL once typing stops.
  const [searchInput, setSearchInput] = useState(filters.search ?? '');
  useEffect(() => {
    if ((filters.search ?? '') === searchInput.trim()) return;
    const timer = setTimeout(() => update({ search: searchInput.trim() }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // Keyed on what it reads; `update` is recreated each render and is not a reason to re-run.
  }, [searchInput, filters.search]);

  const query: InteractionListQuery = { ...filters, page, pageSize };
  const { data, isLoading, isError, error, refetch } = useGetInteractionsQuery(query, {
    pollingInterval: POLL_MS,
  });
  const { data: lines } = useGetLinesQuery();

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // The page can outrun the data when rows disappear beneath it. Clamp rather
  // than show an empty table.
  useEffect(() => {
    if (data !== undefined && page > pageCount) update({ page: String(pageCount) }, true);
  }, [data, page, pageCount]);

  const filtering = Object.keys(filters).length > 0;
  const listSearch = params.toString();

  const open = (id: string): void =>
    void navigate({ pathname: `/interactions/${id}`, search: listSearch });
  const close = (): void => void navigate({ pathname: '/interactions', search: listSearch });

  /**
   * The export link, carrying the same filter the table is showing.
   *
   * An anchor rather than a fetch: the request is same-origin so the session
   * cookie goes with it, and the browser takes the file. Paging is left off —
   * the export is the whole filtered set, not the page.
   */
  const exportHref = useMemo(() => {
    const exportParams = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (typeof value === 'string' && value !== '') exportParams.set(key, value);
    }
    const encoded = exportParams.toString();
    return `/api/interactions/export${encoded ? `?${encoded}` : ''}`;
  }, [filters]);

  return (
    <section className={styles.view}>
      <div className={styles.toolbar}>
        <div className={styles.search}>
          <IconSearch />
          <input
            type="search"
            placeholder="Search summary, name, caller or outcome…"
            value={searchInput}
            aria-label="Search interactions"
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>

        <select
          className={styles.select}
          value={filters.reason ?? ''}
          aria-label="Filter by reason"
          onChange={(event) => update({ reason: event.target.value })}
        >
          <option value="">All reasons</option>
          {CALL_REASON_FILTERS.map((value) => (
            <option key={value} value={value}>
              {callReasonLabels[value]}
            </option>
          ))}
        </select>

        <select
          className={styles.select}
          value={filters.resolution ?? ''}
          aria-label="Filter by resolution"
          onChange={(event) => update({ resolution: event.target.value })}
        >
          <option value="">All resolutions</option>
          {CALL_RESOLUTION_FILTERS.map((value) => (
            <option key={value} value={value}>
              {callResolutionLabels[value]}
            </option>
          ))}
        </select>

        {/* Only worth a control when there is a choice to make. */}
        {(lines?.items.length ?? 0) > 1 && (
          <select
            className={styles.select}
            value={filters.lineId ?? ''}
            aria-label="Filter by line"
            onChange={(event) => update({ lineId: event.target.value })}
          >
            <option value="">All lines</option>
            {lines?.items.map((line) => (
              <option key={line.id} value={line.id}>
                {line.label ?? line.number}
                {line.active ? '' : ' (retired)'}
              </option>
            ))}
          </select>
        )}

        <label className={styles.date}>
          <span>From</span>
          <input
            type="date"
            value={filters.from ?? ''}
            max={filters.to}
            onChange={(event) => update({ from: event.target.value })}
          />
        </label>
        <label className={styles.date}>
          <span>To</span>
          <input
            type="date"
            value={filters.to ?? ''}
            min={filters.from}
            onChange={(event) => update({ to: event.target.value })}
          />
        </label>

        {filtering && (
          <button
            type="button"
            className={styles.clear}
            onClick={() => {
              setSearchInput('');
              update({
                search: null,
                reason: null,
                resolution: null,
                lineId: null,
                from: null,
                to: null,
              });
            }}
          >
            Clear filters
          </button>
        )}

        {/* Disabled with nothing to export rather than hidden: the affordance
            should stay where the operator expects it. */}
        <a
          className={`${styles.export} ${total === 0 ? styles.exportOff : ''}`}
          href={total === 0 ? undefined : exportHref}
          aria-disabled={total === 0}
          title={
            total === 0
              ? 'Nothing to export'
              : `Download ${String(total)} interaction${total === 1 ? '' : 's'} as CSV`
          }
        >
          <IconDownload />
          Export
        </a>
      </div>

      <div className={styles.panel}>
        {isLoading && <div className={styles.state}>Loading interactions…</div>}

        {isError && (
          <div className={styles.state}>
            <p>{messageOf(error)}</p>
            <button type="button" className={styles.retry} onClick={() => void refetch()}>
              Try again
            </button>
          </div>
        )}

        {!isLoading && !isError && total === 0 && (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>
              <IconInteractions />
            </div>
            {filtering ? (
              <>
                <h3>Nothing matches</h3>
                <p>No analysed call matches these filters.</p>
              </>
            ) : (
              <>
                <h3>No interactions yet</h3>
                {/* Deliberately explicit about why the page is empty, so an
                    operator who has taken calls learns analysis is the step
                    still to come rather than assuming they were lost. */}
                <p>
                  A call appears here once the caller has spoken and the call has been analysed.
                  Abandoned calls count on the dashboard instead.
                </p>
              </>
            )}
          </div>
        )}

        {!isLoading && !isError && total > 0 && (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">Line</th>
                    <th scope="col">Caller</th>
                    <th scope="col">Reason</th>
                    <th scope="col">Resolution</th>
                    <th scope="col">Sentiment</th>
                    <th scope="col">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const bar = barGeometry(row.sentiment.score);
                    return (
                      <tr
                        key={row.id}
                        className={`${styles.row} ${row.id === interactionId ? styles.rowOpen : ''}`}
                        tabIndex={0}
                        role="button"
                        aria-label={`Open the call from ${row.callerNumber ?? 'an unknown caller'}`}
                        onClick={() => open(row.id)}
                        onKeyDown={(event) => {
                          // A row that opens on click must open from the keyboard
                          // too, or the drawer is unreachable without a mouse.
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            open(row.id);
                          }
                        }}
                      >
                        <td className={styles.when}>{when(row.callAt ?? row.analyzedAt)}</td>
                        <td className={styles.mono}>
                          {row.line?.label ?? row.line?.number ?? '—'}
                        </td>
                        <td>
                          {row.callerNumber === null ? (
                            <span className={styles.notProvided}>Not provided</span>
                          ) : (
                            <span className={styles.mono}>{row.callerNumber}</span>
                          )}
                        </td>
                        <td>
                          <ReasonChip reason={row.reason} />
                        </td>
                        <td>
                          <ResolutionChip resolution={row.resolution} />
                        </td>
                        <td>
                          <div className={styles.senti}>
                            <div className={styles.track} aria-hidden="true">
                              <span className={styles.centre} />
                              <span
                                className={`${styles.fill} ${styles[bar.tone]}`}
                                style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
                              />
                            </div>
                            <small className={styles[bar.tone]}>
                              {formatScore(row.sentiment.score)}
                            </small>
                          </div>
                        </td>
                        <td className={styles.mono}>{formatDuration(row.durationMs)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPage={(next) => update({ page: String(next) }, true)}
              onPageSize={(size) => update({ pageSize: String(size) })}
            />
          </>
        )}
      </div>

      <InteractionDrawer interactionId={interactionId ?? null} onClose={close} />
    </section>
  );
}
